import { useCallback, useEffect, useRef, useState } from 'react'

import { DynamicFormFieldFragment } from 'generated/resources/graphql'

import { DynamicFormErrors, DynamicFormValues } from 'modules/dynamic_forms/types'

import { validateDynamicFormValues } from 'modules/dynamic_forms/lib/validation'

interface UseDynamicFormStateOptions {
  /** Initial form values. Whenever this reference changes, the form state is re-initialized. */
  initialValues?: DynamicFormValues
}

/**
 * Manages dynamic form state: values, errors, and validation.
 *
 * Provides a simple state container for dynamic form fields that can be
 * composed into any existing form workflow.
 *
 * When `initialValues` changes (by reference), the internal state is
 * re-hydrated with the new values. This enables async data loading
 * (e.g. from a GraphQL query) to populate the form once data arrives.
 *
 * Usage:
 * ```tsx
 * const { values, errors, handleChange, handleToggle, validate } = useDynamicFormState()
 *
 * <DynamicFormFields
 *   fields={fields}
 *   values={values}
 *   onChange={handleChange}
 *   onToggle={handleToggle}
 *   errors={errors}
 * />
 * ```
 */
export function useDynamicFormState({ initialValues = {} }: UseDynamicFormStateOptions = {}) {
  const [values, setValues] = useState<DynamicFormValues>(initialValues)
  const [errors, setErrors] = useState<DynamicFormErrors>({})
  const prevInitialRef = useRef(initialValues)

  // Re-hydrate form values when initialValues reference changes (e.g. query loaded)
  useEffect(() => {
    if (prevInitialRef.current !== initialValues) {
      prevInitialRef.current = initialValues
      setValues(initialValues)
      setErrors({})
    }
  }, [initialValues])

  const handleChange = useCallback((fieldName: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }))
    // Clear error for the field being changed
    setErrors((prev) => {
      if (!prev[fieldName]) return prev
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }, [])

  const handleToggle = useCallback((fieldName: string, value: boolean) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }))
    setErrors((prev) => {
      if (!prev[fieldName]) return prev
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }, [])

  const validate = useCallback(
    (fields: ReadonlyArray<DynamicFormFieldFragment>): boolean => {
      const validationErrors = validateDynamicFormValues(fields, values)
      setErrors(validationErrors)
      return Object.keys(validationErrors).length === 0
    },
    [values]
  )

  const setFieldError = useCallback((fieldName: string, message: string) => {
    setErrors((prev) => ({ ...prev, [fieldName]: message }))
  }, [])

  const resetErrors = useCallback(() => {
    setErrors({})
  }, [])

  return {
    values,
    errors,
    handleChange,
    handleToggle,
    validate,
    setFieldError,
    resetErrors,
    setValues,
  }
}
